import { useState } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { TYPO } from "styles/Typo";
import Input from "components/common/inputs";
import useLogin, { callbackHandler, handleLogin } from "utils/join/login";
import SocialLogin from "components/templates/common/Join/SocialLogin";
import Button from "components/common/buttons/buttons";
import PasswordModal from "components/modals/PasswordModal";
import LoginSubButtons from "components/templates/common/Join/LoginSubButtons";
import Image from "next/image";
import Icon, { ICONS } from "components/common/Icons";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "lib/analytics/react/hooks";

interface Provider {
  id: string;
  name: string;
}

interface Props {
  providers: Record<string, Provider>;
  isMobile: boolean;
  isIAB: boolean;
}

const Login = ({ providers, isMobile, isIAB }: Props) => {
  const { data: session, status } = useSession();
  const router = useRouter();
  const [showModal, setShowModal] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const { t } = useTranslation("login");
  const [isEmailInputOpen, setIsEmailInputOpen] = useState(false);
  const { from, parent_intent_type }: any = router.query;

  const auth = useAuth();

  useLogin({ router, session, status, t, setMessage });

  return (
    <main
      className="min-w-[360px] w-screen max-w-[800px]  bg-white  pb-[72px]"
      style={{
        minHeight: isMobile ? "100vh" : "calc(100vh - 80px)",
      }}
    >
      {isMobile ? (
        <div className="px-2 py-[10px]">
          <div
            className="p-[6px] w-fit cursor-pointer"
            onClick={() => router.back()}
          >
            <Icon name={ICONS.BACK} width={24} height={24} fill="#000" />
          </div>
        </div>
      ) : null}

      <div className="w-full flex-col justify-start items-center  inline-flex ">
        <div className={` flex-col px-6 py-[48px] flex gap-2 `}>
          <Logo />
          <p
            className={`${TYPO.TYPO7_MD} text-gray60 whitespace-pre-line text-center`}
          >
            {`모두에게 평등한 투자 정보\n리스크웨더에서`}
          </p>
        </div>
        <div className="px-6 flex flex-col gap-[10px] py-9 w-full">
          <SocialLogin
            providers={providers}
            callbackHandler={callbackHandler}
            from={from}
            isIAB={isIAB}
            auth={auth}
          />
        </div>
        <div className="px-6 w-full">
          <div className="w-full flex items-center">
            <hr className="flex-grow border-t border-gray-300" />
            <div className="px-2 bg-white">
              <button
                className={`${TYPO.TYPO7_MD} text-gray70 flex gap-2 items-center border-hairlineBorder border py-2 rounded-[42px] px-3 cursor-pointer`}
                onClick={() => setIsEmailInputOpen(!isEmailInputOpen)}
                aria-expanded={isEmailInputOpen}
              >
                <span>이메일로 계속하기</span>
                <motion.div
                  animate={{ rotate: isEmailInputOpen ? 180 : 0 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                >
                  <Arrow />
                </motion.div>
              </button>
            </div>

            <hr className="flex-grow border-t border-gray-300" />
          </div>
        </div>

        {/** 토글 컴포넌트 */}
        <AnimatePresence initial={false}>
          {isEmailInputOpen && (
            <motion.div
              className=" w-full"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              style={{ overflow: "hidden" }}
            >
              <div className=" w-full px-6 py-9">
                <div className="flex-col justify-start items-center gap-3 inline-flex w-full">
                  <div className="h-[166px] flex-col justify-start items-start gap-6 inline-flex w-full">
                    {/** 이메일 비밀번호 인풋 */}
                    <div className="h-[94px] flex-col justify-start items-start gap-3.5 inline-flex w-full">
                      <Input
                        placeholder={t("email")}
                        type="line"
                        size="md"
                        value={email}
                        setValue={setEmail}
                        width={"w-full"}
                      />
                      <Input
                        placeholder={t("password")}
                        type="line"
                        size="md"
                        value={password}
                        setValue={setPassword}
                        width={"w-full"}
                        password={true}
                      />
                    </div>
                    {/** 로그인 버튼 */}
                    <Button
                      clickHandler={() => {
                        auth.start(
                          {
                            ui: "page",
                            entry_reason: from ? "gate" : "direct",
                            method_kind: "password",
                            mode_pred: "login",
                            provider: null,
                            source_key: "login_email",
                          },
                          { parentIntentType: parent_intent_type }
                        );
                        handleLogin({
                          router,
                          email,
                          password,
                          setMessage,
                          from,
                        });
                      }}
                      text={t("loginButton")}
                      type={"primary"}
                      size={"lg"}
                      width="w-full"
                    />
                  </div>
                  {message && (
                    <p className={`${TYPO.TYPO7_MD} text-red40`}>{message}</p>
                  )}
                  {/** 비밀번호 재설정 및 회원가입 */}
                  <LoginSubButtons setShowModal={setShowModal} t={t} />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {showModal && (
          <PasswordModal
            isOpen={showModal}
            closeModal={() => setShowModal(false)}
          />
        )}
      </div>
    </main>
  );
};

const Logo = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="221"
    height="42"
    viewBox="0 0 221 42"
    fill="none"
  >
    <path
      d="M35.239 23.9476C34.9048 23.5921 34.4337 23.3679 33.9023 23.3679C32.8944 23.3679 32.0781 24.1828 32.0781 25.189C32.0781 25.7249 32.3082 26.2062 32.6807 26.5398C32.8834 26.7421 32.993 27.0101 32.993 27.2945C32.993 27.8905 32.5109 28.3718 31.9138 28.3718C31.6782 28.3718 31.4536 28.2952 31.2729 28.1585L18.6459 15.5531C21.982 13.0648 24.5403 13.1359 25.7729 13.4039C26.9726 13.6664 27.9258 14.3171 28.3914 15.1976C29.3775 17.0734 28.9228 18.5062 28.7968 18.8289C28.6763 19.0749 28.605 19.3484 28.605 19.6382C28.605 20.6445 29.4213 21.4593 30.4292 21.4593C31.114 21.4593 31.7111 21.082 32.0234 20.5242C32.626 19.3539 33.2231 16.5484 31.6125 13.4968C30.6484 11.6703 28.8022 10.3359 26.5453 9.84372C24.6663 9.43356 20.8262 9.26948 16.2246 12.8023C16.1588 12.8515 16.0986 12.9007 16.0328 12.9554L13.365 10.2921C13.0308 9.9312 12.5543 9.707 12.0229 9.707C11.0149 9.707 10.1987 10.5218 10.1987 11.5281C10.1987 12.0585 10.4233 12.5343 10.7848 12.8679L13.4527 15.5312C11.0094 18.5773 9.74948 22.3617 10.2754 25.6101C10.7355 28.4757 12.5214 30.5921 15.3043 31.5601C16.2465 31.8882 17.1942 32.0523 18.109 32.0523C19.9661 32.0523 21.7191 31.3905 23.1434 30.0999C24.0363 29.296 24.7266 28.3062 25.1867 27.2452L28.5612 30.614L28.5503 30.6249C29.4432 31.5273 30.6374 32.0195 31.9083 32.0195C34.5159 32.0195 36.6359 29.903 36.6359 27.2999C36.6359 26.0421 36.1264 24.8281 35.2335 23.9476H35.239ZM20.7002 27.4038C19.9058 28.1202 18.5308 28.8312 16.5149 28.1257C15.0139 27.6007 14.1319 26.5617 13.8854 25.0359C13.5513 22.9468 14.4332 20.3328 16.0712 18.1398L22.1847 24.2429C22.2012 25.2984 21.6205 26.5726 20.7002 27.4038Z"
      fill="#3182F6"
    />
    <path
      d="M55.6477 32.0384L51.2816 24.0978H49.4081V32.0384H45.4365V12.0557H52.8648C54.3987 12.0557 55.7025 12.3236 56.7762 12.8596C57.8554 13.3955 58.6606 14.1229 59.203 15.0361C59.7398 15.9494 60.0137 16.9721 60.0137 18.1041C60.0137 19.4057 59.6357 20.576 58.8798 21.6205C58.1238 22.665 57.0008 23.3869 55.5053 23.7806L60.2438 32.033H55.6477V32.0384ZM49.4081 21.1283H52.7279C53.807 21.1283 54.6069 20.8713 55.1382 20.3518C55.6696 19.8322 55.9325 19.1103 55.9325 18.1916C55.9325 17.2728 55.6696 16.5838 55.1382 16.0861C54.6069 15.5885 53.807 15.3369 52.7279 15.3369H49.4081V21.1283Z"
      fill="#191F28"
    />
    <path
      d="M63.0373 13.6074C62.5716 13.1644 62.3416 12.6121 62.3416 11.9559C62.3416 11.2996 62.5716 10.7473 63.0373 10.3043C63.5029 9.86132 64.0836 9.64258 64.7793 9.64258C65.475 9.64258 66.0612 9.86679 66.5213 10.3043C66.987 10.7473 67.2171 11.2996 67.2171 11.9559C67.2171 12.6121 66.987 13.1644 66.5213 13.6074C66.0557 14.0504 65.475 14.2691 64.7793 14.2691C64.0836 14.2691 63.4974 14.0504 63.0373 13.6074ZM66.7405 16.1394V32.0535H62.7689V16.1394H66.7405Z"
      fill="#191F28"
    />
    <path
      d="M72.3466 31.3549C71.3277 30.8955 70.5169 30.2666 69.9198 29.4736C69.3227 28.6806 68.9995 27.8056 68.9392 26.8431H72.9382C73.0149 27.4447 73.3108 27.9478 73.8312 28.3416C74.3516 28.7353 74.998 28.9377 75.7759 28.9377C76.5538 28.9377 77.1235 28.7845 77.5508 28.4838C77.9781 28.183 78.1862 27.7947 78.1862 27.3244C78.1862 26.8158 77.9288 26.433 77.4084 26.1814C76.8879 25.9244 76.0608 25.651 74.9268 25.3502C73.7545 25.0658 72.7958 24.776 72.0508 24.4752C71.3058 24.1744 70.6594 23.7096 70.1225 23.0916C69.5857 22.4736 69.3118 21.6314 69.3118 20.576C69.3118 19.7119 69.5637 18.9189 70.0623 18.2025C70.5608 17.4861 71.2839 16.9228 72.2151 16.5072C73.1519 16.0916 74.253 15.8838 75.5184 15.8838C77.3919 15.8838 78.882 16.3486 79.9995 17.2838C81.117 18.2189 81.7306 19.4767 81.8401 21.0572H78.0383C77.9836 20.4338 77.7206 19.9416 77.2605 19.5752C76.7948 19.2088 76.1758 19.0228 75.4034 19.0228C74.6857 19.0228 74.1325 19.1541 73.7435 19.4166C73.3546 19.6791 73.1628 20.0455 73.1628 20.5213C73.1628 21.0463 73.4258 21.451 73.9572 21.7244C74.4885 21.9978 75.3103 22.2767 76.4223 22.5556C77.5563 22.84 78.493 23.1299 79.2326 23.4306C79.9721 23.7314 80.6076 24.1963 81.1444 24.8306C81.6813 25.4596 81.9607 26.2963 81.9826 27.3299C81.9826 28.2322 81.7306 29.0471 81.2321 29.758C80.7336 30.4744 80.0105 31.0322 79.0792 31.4424C78.1424 31.847 77.0523 32.0494 75.8033 32.0494C74.5543 32.0494 73.3655 31.8197 72.3411 31.3549H72.3466Z"
      fill="#191F28"
    />
    <path
      d="M93.7201 32.0494L88.3899 25.126V32.0494H84.4183V10.8799H88.3899V22.7799L93.6653 16.1353H98.8257L91.9068 23.9939L98.8804 32.0494H93.7201Z"
      fill="#191F28"
    />
    <path
      d="M121.886 16.1384L117.29 32.0525H113.006L110.141 20.858L107.276 32.0525H102.965L98.3412 16.1384H102.368L105.145 28.0658L108.152 16.1384H112.349L115.296 28.0384L118.073 16.1384H121.886Z"
      fill="#191F28"
    />
    <path
      d="M137.692 25.1595H126.204C126.298 26.2915 126.697 27.1774 127.393 27.8173C128.094 28.4571 128.954 28.7798 129.973 28.7798C131.447 28.7798 132.499 28.1509 133.123 26.8876H137.407C136.952 28.397 136.081 29.6329 134.8 30.6063C133.512 31.5743 131.935 32.061 130.061 32.061C128.549 32.061 127.191 31.7274 125.991 31.0602C124.791 30.393 123.854 29.447 123.181 28.222C122.507 26.997 122.173 25.586 122.173 23.9837C122.173 22.3813 122.501 20.9431 123.164 19.7181C123.827 18.4931 124.753 17.5524 125.942 16.8907C127.13 16.229 128.505 15.9009 130.056 15.9009C131.606 15.9009 132.888 16.2235 134.071 16.8634C135.254 17.5032 136.169 18.411 136.821 19.5923C137.473 20.7681 137.802 22.1243 137.802 23.6501C137.802 24.2134 137.763 24.722 137.687 25.1759L137.692 25.1595ZM133.693 22.5017C133.677 21.4845 133.304 20.6696 132.586 20.0571C131.869 19.4446 130.987 19.1384 129.951 19.1384C128.965 19.1384 128.138 19.4337 127.47 20.0298C126.796 20.6259 126.385 21.4462 126.237 22.5017H133.698H133.693Z"
      fill="#191F28"
    />
    <path
      d="M139.772 19.6993C140.407 18.4743 141.267 17.5336 142.352 16.8719C143.437 16.2102 144.653 15.8821 145.995 15.8821C147.167 15.8821 148.192 16.1172 149.074 16.5876C149.95 17.0579 150.657 17.6539 151.188 18.3704V16.1391H155.187V32.0532H151.188V29.5102C150.679 30.243 149.972 30.85 149.074 31.3313C148.175 31.8125 147.14 32.0532 145.968 32.0532C144.642 32.0532 143.437 31.7141 142.352 31.036C141.262 30.3579 140.402 29.4008 139.772 28.1649C139.137 26.9289 138.824 25.5125 138.824 23.9102C138.824 22.3079 139.142 20.9243 139.772 19.6993ZM150.619 21.493C150.241 20.804 149.731 20.279 149.085 19.9125C148.444 19.5461 147.754 19.3602 147.014 19.3602C146.274 19.3602 145.595 19.5407 144.971 19.8961C144.346 20.2516 143.842 20.7766 143.453 21.4657C143.064 22.1547 142.873 22.9696 142.873 23.9102C142.873 24.8508 143.064 25.6766 143.453 26.3821C143.842 27.0876 144.352 27.6289 144.987 28.0063C145.623 28.3836 146.296 28.5696 147.014 28.5696C147.732 28.5696 148.444 28.3836 149.085 28.0172C149.726 27.6508 150.235 27.1204 150.619 26.4368C150.997 25.7477 151.188 24.9219 151.188 23.9649C151.188 23.0079 150.997 22.1821 150.619 21.493Z"
      fill="#191F28"
    />
    <path
      d="M162.843 19.6475V27.2217C162.843 27.7467 162.969 28.1295 163.227 28.3647C163.484 28.5998 163.911 28.7201 164.519 28.7201H166.36V32.0561H163.862C160.515 32.0561 158.844 30.4373 158.844 27.1943V19.6475H156.971V16.3991H158.844V12.5272H162.843V16.3991H166.36V19.6475H162.843Z"
      fill="#191F28"
    />
    <path
      d="M180.388 16.6911C181.314 17.2107 182.037 17.9708 182.557 18.9825C183.078 19.9888 183.335 21.2028 183.335 22.6138V32.0528H179.363V23.1497C179.363 21.9083 179.051 20.9513 178.427 20.2786C177.802 19.6114 176.953 19.2778 175.874 19.2778C174.795 19.2778 173.913 19.6114 173.277 20.2786C172.642 20.9458 172.33 21.9028 172.33 23.1497V32.0528H168.358V10.8833H172.33V18.0911C172.839 17.413 173.518 16.8825 174.373 16.4942C175.222 16.106 176.17 15.9146 177.211 15.9146C178.399 15.9146 179.462 16.1716 180.388 16.6911Z"
      fill="#191F28"
    />
    <path
      d="M200.77 25.1595H189.283C189.376 26.2915 189.776 27.1774 190.472 27.8173C191.173 28.4571 192.033 28.7798 193.052 28.7798C194.525 28.7798 195.577 28.1509 196.202 26.8876H200.486C200.031 28.397 199.16 29.6329 197.878 30.6063C196.591 31.5743 195.013 32.061 193.139 32.061C191.628 32.061 190.269 31.7274 189.069 31.0602C187.87 30.393 186.933 29.447 186.259 28.222C185.585 26.997 185.251 25.586 185.251 23.9837C185.251 22.3813 185.58 20.9431 186.243 19.7181C186.905 18.4931 187.831 17.5524 189.02 16.8907C190.209 16.229 191.584 15.9009 193.134 15.9009C194.684 15.9009 195.966 16.2235 197.149 16.8634C198.333 17.5032 199.248 18.411 199.899 19.5923C200.551 20.7681 200.88 22.1243 200.88 23.6501C200.88 24.2134 200.842 24.722 200.765 25.1759L200.77 25.1595ZM196.771 22.5017C196.755 21.4845 196.383 20.6696 195.665 20.0571C194.947 19.4446 194.065 19.1384 193.03 19.1384C192.044 19.1384 191.217 19.4337 190.548 20.0298C189.875 20.6259 189.464 21.4462 189.316 22.5017H196.777H196.771Z"
      fill="#191F28"
    />
    <path
      d="M208.896 16.6251C209.718 16.1548 210.66 15.9197 211.717 15.9197V20.0759H210.665C209.416 20.0759 208.48 20.3658 207.844 20.9509C207.209 21.5361 206.896 22.5533 206.896 24.0025V32.0579H202.925V16.1439H206.896V18.572C207.406 17.7408 208.074 17.0955 208.896 16.6197V16.6251Z"
      fill="#191F28"
    />
  </svg>
);

const Arrow = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="13"
    height="12"
    viewBox="0 0 13 12"
    fill="none"
  >
    <path
      d="M6.52911 7.6375L10.2041 3.9625C10.3291 3.8375 10.4749 3.77708 10.6416 3.78125C10.8083 3.78542 10.9541 3.85 11.0791 3.975C11.2041 4.1 11.2666 4.24583 11.2666 4.4125C11.2666 4.57917 11.2041 4.725 11.0791 4.85L7.24161 8.7C7.14161 8.8 7.02911 8.875 6.90411 8.925C6.77911 8.975 6.65411 9 6.52911 9C6.40411 9 6.27911 8.975 6.15411 8.925C6.02911 8.875 5.91661 8.8 5.81661 8.7L1.96661 4.85C1.84161 4.725 1.78119 4.57708 1.78536 4.40625C1.78952 4.23542 1.85411 4.0875 1.97911 3.9625C2.10411 3.8375 2.24994 3.775 2.41661 3.775C2.58327 3.775 2.72911 3.8375 2.85411 3.9625L6.52911 7.6375Z"
      fill="#4E5968"
    />
  </svg>
);

export default Login;
